/**
 * PATH: packages/ui/src/kanban/utils/boardSlug.ts
 * FILE_ID: UI-KANBAN-UTIL-SLUG-001
 * PURPOSE: Single source of truth for "what's the slug for a new
 *          board with name X?".
 *
 * WHY:
 *   The backend Board model declares `slug = models.SlugField(...)`
 *   with no `null=True` and no auto-derivation in the DRF
 *   serializer, so every create-board client MUST send a slug or
 *   it gets a 400 with `{"slug": ["This field is required."]}`
 *   (operator complaint 2026-05-19: *"Then I tried duplicating
 *   the Board.. it didn't work… slug: This field is required"*).
 *
 *   The `CreateBoardDialog` flow used to inline its own slug
 *   derivation. The `duplicateBoard` helper forgot to derive
 *   one, sent a payload without it, and operators got a 400 with
 *   a confusing error every time they right-clicked
 *   "Duplicate board". This module extracts the canonical
 *   slug-from-name derivation so every board-creation surface
 *   shares the same shape and a new caller (template clone,
 *   bulk create, etc.) can't drift.
 *
 * INPUT  → OUTPUT
 *   slugifyBoardName(name: string)        → string (no random suffix)
 *   buildUniqueBoardSlug(name: string)    → string (with random suffix,
 *                                                   suitable for sending
 *                                                   to the API directly)
 *
 * The two-function split lets tests pin the deterministic part
 * (`slugifyBoardName`) without dealing with `Math.random`.
 */

/**
 * Pure normalisation: lowercase, kebab, max 60 chars, fallback to
 * `'board'` if the input has no slug-safe characters. Mirrors
 * Django's `slugify` for the common case; we don't need full
 * unicode handling because the backend's SlugField re-validates.
 */
export function slugifyBoardName(name: string): string {
  return (
    name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'board'
  )
}

/**
 * Add a short random suffix to the deterministic slug so two
 * boards with the same name in the same org don't collide on the
 * backend's `UniqueConstraint(organisation, slug)`. Length tuned
 * to ~4 chars of base36 ≈ 1.6m combinations — collision-free at
 * realistic per-org board counts (Bravada is ~30 today).
 *
 * Returns a string suitable to pass straight to the API's
 * `slug` field.
 */
export function buildUniqueBoardSlug(name: string): string {
  const base = slugifyBoardName(name)
  const suffix = Math.random().toString(36).slice(2, 6)
  return `${base}-${suffix}`
}
